// GPS Simulator for testing boat tracking without actual GPS hardware
// Simulates a boat trip around Tiritiri Matangi Island in Hauraki Gulf

export interface SimulatedPosition {
  coords: {
    latitude: number
    longitude: number
    accuracy: number
    heading: number | null
    speed: number | null
    altitude: number | null
    altitudeAccuracy: number | null
  }
  timestamp: number
}

// Predefined route: Circuit around Tiritiri Matangi Island
// Starting from Whangaparaoa, circling the island, and returning
const ROUTE_POINTS: [number, number][] = [
  [174.7580, -36.5650], // Start: Whangaparaoa Peninsula
  [174.7680, -36.5720], // Heading northeast
  [174.7820, -36.5800], // Approaching Tiritiri
  [174.7950, -36.5850], // North side of island
  [174.8020, -36.5920], // Northeast point
  [174.8050, -36.6000], // East side
  [174.8030, -36.6080], // Southeast point
  [174.7950, -36.6120], // South side
  [174.7850, -36.6100], // Southwest point
  [174.7750, -36.6050], // West side
  [174.7700, -36.6000], // Northwest approach
  [174.7720, -36.5920], // Heading back
  [174.7680, -36.5850], // Return path
  [174.7620, -36.5780], // Approaching Whangaparaoa
  [174.7580, -36.5700], // Back to start
  [174.7580, -36.5650], // Complete circuit
]

export class GPSSimulator {
  private currentIndex: number = 0
  private interpolationProgress: number = 0
  private speed: number = 5.14444 // 10 knots in m/s (typical boat cruising speed)
  private updateInterval: number = 1000 // Update every 1 second
  private lastUpdateTime: number = Date.now()
  private watchId: number = 0
  private callbacks: Map<number, (position: SimulatedPosition) => void> = new Map()
  private errorCallbacks: Map<number, (error: GeolocationPositionError) => void> = new Map()
  private intervalId: number | null = null

  constructor(speedKnots: number = 10) {
    this.speed = speedKnots * 0.514444 // Convert knots to m/s
  }

  /**
   * Calculate distance between two lat/lon points using Haversine formula
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371000 // Earth radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * Calculate bearing between two points
   */
  private calculateBearing(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const y = Math.sin(dLon) * Math.cos((lat2 * Math.PI) / 180)
    const x =
      Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
      Math.sin((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.cos(dLon)
    const bearing = (Math.atan2(y, x) * 180) / Math.PI
    return (bearing + 360) % 360 // Normalize to 0-360
  }

  /**
   * Interpolate between two points based on progress (0-1)
   */
  private interpolate(
    point1: [number, number],
    point2: [number, number],
    progress: number
  ): [number, number] {
    const lon = point1[0] + (point2[0] - point1[0]) * progress
    const lat = point1[1] + (point2[1] - point1[1]) * progress
    return [lon, lat]
  }

  /**
   * Get current simulated position
   */
  private getCurrentPosition(): SimulatedPosition {
    const currentPoint = ROUTE_POINTS[this.currentIndex]
    const nextIndex = (this.currentIndex + 1) % ROUTE_POINTS.length
    const nextPoint = ROUTE_POINTS[nextIndex]

    const [lon, lat] = this.interpolate(currentPoint, nextPoint, this.interpolationProgress)
    const heading = this.calculateBearing(currentPoint[1], currentPoint[0], nextPoint[1], nextPoint[0])

    return {
      coords: {
        latitude: lat,
        longitude: lon,
        accuracy: 5 + Math.random() * 3, // Simulate GPS accuracy 5-8m (good GPS)
        heading: heading,
        speed: this.speed + (Math.random() - 0.5) * 0.5, // Add slight speed variation
        altitude: null,
        altitudeAccuracy: null,
      },
      timestamp: Date.now(),
    }
  }

  /**
   * Update position based on elapsed time
   */
  private updatePosition() {
    const now = Date.now()
    const elapsed = (now - this.lastUpdateTime) / 1000 // seconds
    this.lastUpdateTime = now

    const currentPoint = ROUTE_POINTS[this.currentIndex]
    const nextIndex = (this.currentIndex + 1) % ROUTE_POINTS.length
    const nextPoint = ROUTE_POINTS[nextIndex]

    // Calculate distance to next point
    const segmentDistance = this.calculateDistance(
      currentPoint[1],
      currentPoint[0],
      nextPoint[1],
      nextPoint[0]
    )

    // Calculate how much progress we make this update
    const distanceTraveled = this.speed * elapsed
    const progressIncrement = distanceTraveled / segmentDistance

    this.interpolationProgress += progressIncrement

    // Move to next segment if we've completed this one
    if (this.interpolationProgress >= 1) {
      this.currentIndex = nextIndex
      this.interpolationProgress = 0
    }

    // Notify all callbacks
    const position = this.getCurrentPosition()
    this.callbacks.forEach((callback) => {
      callback(position)
    })
  }

  /**
   * Start simulating GPS updates (mimics navigator.geolocation.watchPosition)
   */
  public watchPosition(
    successCallback: (position: SimulatedPosition) => void,
    _errorCallback?: (error: GeolocationPositionError) => void,
    _options?: PositionOptions
  ): number {
    this.watchId++
    const id = this.watchId

    this.callbacks.set(id, successCallback)
    if (_errorCallback) {
      this.errorCallbacks.set(id, _errorCallback)
    }

    // Start the simulation loop if not already running
    if (!this.intervalId) {
      // Send initial position immediately
      successCallback(this.getCurrentPosition())

      // Start update loop
      this.intervalId = window.setInterval(() => {
        this.updatePosition()
      }, this.updateInterval)
    } else {
      // If already running, send current position immediately
      successCallback(this.getCurrentPosition())
    }

    return id
  }

  /**
   * Stop watching position (mimics navigator.geolocation.clearWatch)
   */
  public clearWatch(watchId: number) {
    this.callbacks.delete(watchId)
    this.errorCallbacks.delete(watchId)

    // Stop simulation if no more callbacks
    if (this.callbacks.size === 0 && this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * Get current position once (mimics navigator.geolocation.getCurrentPosition)
   */
  public getCurrentPositionOnce(
    successCallback: (position: SimulatedPosition) => void,
    _errorCallback?: (error: GeolocationPositionError) => void,
    _options?: PositionOptions
  ) {
    successCallback(this.getCurrentPosition())
  }

  /**
   * Reset simulator to start of route
   */
  public reset() {
    this.currentIndex = 0
    this.interpolationProgress = 0
    this.lastUpdateTime = Date.now()
  }

  /**
   * Set boat speed in knots
   */
  public setSpeed(speedKnots: number) {
    this.speed = speedKnots * 0.514444
  }
}

// Global singleton instance
let simulatorInstance: GPSSimulator | null = null

/**
 * Check if simulator mode is enabled via URL parameter
 */
export function isSimulatorEnabled(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.get('simulate') === 'true'
}

/**
 * Get or create the GPS simulator instance
 */
export function getGPSSimulator(): GPSSimulator {
  if (!simulatorInstance) {
    simulatorInstance = new GPSSimulator(10) // 10 knots default
  }
  return simulatorInstance
}
